"use client";

import React, { useMemo } from "react";
import { useGLTF, MeshTransmissionMaterial } from "@react-three/drei";
import { CrystalModelProps } from "@/types/laser-engraving";
import { TRANSMISSION_MATERIAL_ENHANCEMENTS } from "@/lib/laser-physics";
import * as THREE from "three";

/**
 * GLTF 加载水晶模型组件
 */
export const GLTFCrystalModel = React.memo<CrystalModelProps>(
  ({
    modelPath,
    position = [0, 0, 0],
    rotation = [0, 0, 0],
    scale = 1,
    shellConfig,
    castShadow = true,
    receiveShadow = true,
  }) => {
    // 始终调用useGLTF（避免条件性Hook调用），但处理SSR情况
    const gltf = useGLTF(modelPath);

    // 服务端渲染时返回null
    if (typeof window === "undefined") {
      return null;
    }

    // 验证模型是否正确加载
    if (!gltf || !gltf.scene) {
      console.error("🎯 CrystalModel 模型加载失败:", modelPath);
      throw new Error(`模型加载失败: ${modelPath}`);
    }

    const { scene } = gltf;

    // 克隆场景并收集所有mesh，为每个应用材质
    const meshes = useMemo(() => {
      const clonedScene = scene.clone();
      const meshArray: THREE.Mesh[] = [];

      clonedScene.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const mesh = child as THREE.Mesh;
          mesh.castShadow = castShadow;
          mesh.receiveShadow = receiveShadow;
          meshArray.push(mesh);

          // 🎯 确保UV坐标存在（MeshTransmissionMaterial需要）
          if (
            !mesh.geometry.attributes.uv &&
            mesh.geometry.attributes.position
          ) {
            // 为缺少UV坐标的GLB模型自动生成UV坐标
            const geometry = mesh.geometry as THREE.BufferGeometry;
            const uvAttribute = new THREE.BufferAttribute(
              new Float32Array(geometry.attributes.position.count * 2),
              2,
            );

            // 使用球面UV映射算法
            const positions = geometry.attributes.position;
            for (let i = 0; i < positions.count; i++) {
              const x = positions.getX(i);
              const y = positions.getY(i);
              const z = positions.getZ(i);

              // 球面坐标转UV
              const length = Math.sqrt(x * x + y * y + z * z);
              const phi = Math.atan2(z, x);
              const theta = length > 0 ? Math.acos(y / length) : 0;

              const u = (phi + Math.PI) / (2 * Math.PI);
              const v = theta / Math.PI;

              uvAttribute.setXY(i, u, v);
            }

            geometry.setAttribute("uv", uvAttribute);
          }

          // 计算并应用缩放
          if (!mesh.geometry.boundingBox) {
            mesh.geometry.computeBoundingBox();
          }

          const bbox = mesh.geometry.boundingBox;
          if (bbox) {
            const size = {
              width: bbox.max.x - bbox.min.x,
              height: bbox.max.y - bbox.min.y,
              depth: bbox.max.z - bbox.min.z,
            };

            // 计算缩放比例：让模型尺寸匹配水晶容器尺寸
            const maxDimension = Math.max(size.width, size.height, size.depth);
            const targetSize = shellConfig.size;
            const scaleRatio = targetSize / maxDimension;

            // 应用缩放
            mesh.scale.setScalar(scaleRatio);
          }
        }
      });

      return meshArray;
    }, [scene, castShadow, receiveShadow, shellConfig.size]);

    // 如果没有找到mesh，抛出错误
    if (meshes.length === 0) {
      throw new Error(`模型中没有Mesh: ${modelPath}`);
    }

    // 应用正确的坐标系旋转（水晶模型需要X轴旋转90度）
    const finalRotation: [number, number, number] = [
      Math.PI / 2 + (Array.isArray(rotation) ? rotation[0] : 0), // X轴旋转90度
      Array.isArray(rotation) ? rotation[1] : 0,
      Array.isArray(rotation) ? rotation[2] : 0,
    ];

    return (
      <group position={position} rotation={finalRotation} scale={scale}>
        {meshes.map((mesh, index) => (
          <mesh
            key={`${modelPath}-mesh-${index}`}
            geometry={mesh.geometry}
            position={mesh.position}
            rotation={mesh.rotation}
            scale={mesh.scale}
            castShadow={castShadow}
            receiveShadow={receiveShadow}
          >
            {/* 🎯 水晶透射材质 - 强制使用MeshTransmissionMaterial获得最佳效果 */}
            <MeshTransmissionMaterial
              // 基础透射参数
              resolution={TRANSMISSION_MATERIAL_ENHANCEMENTS.resolution}
              transmission={shellConfig.transmission}
              thickness={shellConfig.thickness} // 使用独立的厚度参数
              roughness={shellConfig.roughness}
              ior={shellConfig.ior} // 石英水晶的真实折射率
              chromaticAberration={shellConfig.chromaticAberration}
              distortion={shellConfig.distortion}
              anisotropy={shellConfig.anisotropy}
              color={shellConfig.color}
              // 增强水晶质感的附加参数
              samples={TRANSMISSION_MATERIAL_ENHANCEMENTS.samples}
              temporalDistortion={
                TRANSMISSION_MATERIAL_ENHANCEMENTS.temporalDistortion
              }
              clearcoat={TRANSMISSION_MATERIAL_ENHANCEMENTS.clearcoat}
              clearcoatRoughness={
                TRANSMISSION_MATERIAL_ENHANCEMENTS.clearcoatRoughness
              }
            />
          </mesh>
        ))}
      </group>
    );
  },
);

GLTFCrystalModel.displayName = "CrystalModel";

/**
 * 预加载水晶模型
 * 用于提前加载模型资源，提升用户体验
 */
export function preloadCrystalModel(modelPath: string) {
  useGLTF.preload(modelPath);
}

/**
 * 清理水晶模型缓存
 * 用于释放不再需要的模型资源
 */
export function clearCrystalModelCache(modelPath: string) {
  useGLTF.clear(modelPath);
}

/**
 * 立方体水晶模型组件
 */
export const CubeCrystalModel = React.memo<
  Omit<CrystalModelProps, "modelPath">
>(
  ({
    position = [0, 0, 0],
    rotation = [0, 0, 0],
    scale = 1,
    shellConfig,
    castShadow = true,
    receiveShadow = true,
  }) => {
    return (
      <mesh
        position={position}
        rotation={rotation}
        scale={scale}
        castShadow={castShadow}
        receiveShadow={receiveShadow}
      >
        <boxGeometry
          args={[shellConfig.size, shellConfig.size, shellConfig.size]}
        />
        <MeshTransmissionMaterial
          resolution={TRANSMISSION_MATERIAL_ENHANCEMENTS.resolution}
          transmission={shellConfig.transmission}
          thickness={shellConfig.thickness}
          roughness={shellConfig.roughness}
          ior={shellConfig.ior}
          chromaticAberration={shellConfig.chromaticAberration}
          distortion={shellConfig.distortion}
          anisotropy={shellConfig.anisotropy}
          color={shellConfig.color}
          samples={TRANSMISSION_MATERIAL_ENHANCEMENTS.samples}
          temporalDistortion={
            TRANSMISSION_MATERIAL_ENHANCEMENTS.temporalDistortion
          }
          clearcoat={TRANSMISSION_MATERIAL_ENHANCEMENTS.clearcoat}
          clearcoatRoughness={
            TRANSMISSION_MATERIAL_ENHANCEMENTS.clearcoatRoughness
          }
        />
      </mesh>
    );
  },
);

CubeCrystalModel.displayName = "CubeCrystalModel";
